import React, {useState, useEffect} from 'react';

import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useSelector, useDispatch } from 'react-redux';
import { useMap } from 'react-map-gl';
import { useTranslation } from 'react-i18next';

import { setContentType, setOpenSideBar } from 'handimap-react-lib/store/actions/sideBarActions';
import { selectFeature, unselectFeature } from 'handimap-react-lib/store/actions/selectedFeatureActions';
import { CardRoadStep } from 'handimap-react-lib/components/Card/CardRoadStep';
import { getBounds } from 'handimap-react-lib/utils/useBounds';
import { PointWithLabelRoad } from 'handimap-react-lib/components/PointWithLabelRoad';
import { TransportVisual } from 'handimap-react-lib/components/TransportVisual';
import { PedestrianVisual } from 'handimap-react-lib/components/PedestrianVisual';
import { BackButton } from '../components/BackButton';

export const Roadmap = ({typeAcc, status, allResults, selectedPath, isMobile}) => {
  const {t} = useTranslation();
  const { mymap } = useMap();

  const [selectedFeature, setSelectedFeature] = useState();
  const dispatch = useDispatch();
  const origin = useSelector(state => state.directions.origin);
  const dest = useSelector(state => state.directions.destination);
  const goBack = async() => {
   
    dispatch(unselectFeature());
    dispatch(setOpenSideBar(false));
    // add a timout for mobile mode to avoid strange transition
    if (isMobile) {
      await new Promise(res => setTimeout(res, 50));
    }
    dispatch(setContentType('direction'));
  };
  let features = [];
  let timeDepart = undefined;
  let timeDest = undefined;

  if (status === 'success') {
    if (allResults.length > 0) {
      features = allResults[selectedPath].geojson.features;
      if (allResults[selectedPath].locations) {
        const hourDepart = allResults[selectedPath].locations[0].date_time;
        const hourDest = allResults[selectedPath].locations[1].date_time;
        if (hourDepart && hourDest) {
          timeDepart = hourDepart.slice(hourDepart.indexOf('T') + 1);
          timeDest = hourDest.slice(hourDest.indexOf('T') + 1);
        }
      }
    }
  }

  const hoverAndFocusCardCallBack = (featureID) => {
    dispatch(selectFeature(features[featureID], 'selectedpath_line'));
    setSelectedFeature(featureID);
  };

  const clickCardCallBack = (featureID) => {
    dispatch(selectFeature(features[featureID],'selectedpath_line' ,'stepsDetails'));
    setSelectedFeature(featureID);
    dispatch(setContentType('selectedPathDetails'));
  };

  const flyToPath = (featureID) => {
    const featurePath = {
      type: 'FeatureCollection',
      features: [features[featureID]]
    };
    const bounds = getBounds(featurePath);
    if (bounds !== undefined) {
      setTimeout(() => {
        mymap?.fitBounds(bounds, {
          padding: {
            top: isMobile ? 100 : 50,
            bottom: isMobile ? 100 : 20,
            left: isMobile ? 50: 25,
            right: isMobile ? 50: 25
          }
        });
      }, 100);
    }
  };

  const renderVisualPath = (actualFeature, nextFeature) => {
    const actualProperties = actualFeature.properties;
    switch (actualProperties.m) {
      case 'transit':
        return (
          <TransportVisual properties={actualProperties} />
        );
      default:
        return (
          <PedestrianVisual 
            typeAcc={typeAcc} 
            actualFeature={actualFeature}
            nextFeature={nextFeature} 
          />
        );
    }
  };

  const renderItemList = (i, feature, nextFeature) => {
    if (i === 0) {
      return (
        <React.Fragment>
          <CardRoadStep key={i} active={i === selectedFeature ? 'true' : 'false'}>
            <CardActionArea 
              onMouseEnter={() => hoverAndFocusCardCallBack(i)} 
              onFocus={() => hoverAndFocusCardCallBack(i)} 
              onClick={() => {
                clickCardCallBack(i);
                flyToPath(i);
              }}
            >
              {renderVisualPath(feature, nextFeature)}
            </CardActionArea>
          </CardRoadStep>
        </React.Fragment>
      );
    }
    if (i === features.length - 1) {
      return (
        <></>
      );
    } else {
      return (
        <CardRoadStep key={i} active={i === selectedFeature ? 'true' : 'false'}>
          <CardActionArea 
            onMouseEnter={() => hoverAndFocusCardCallBack(i)} 
            onFocus={() => hoverAndFocusCardCallBack(i)} 
            onClick={() => {
              clickCardCallBack(i);
              flyToPath(i);
            }}
          >
            {renderVisualPath(feature, nextFeature)}
          </CardActionArea>
        </CardRoadStep>
      );
    }
  };

  useEffect(() => {
    dispatch(unselectFeature());
    setSelectedFeature(null);
  }, [status]);

  const styles = {
    container: {
      display: 'flex',
      width: '100%',
      height: '100%',
      backgroundColor: 'transparent'
    },
    headerContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingY: '1em',
      paddingX: '.5em',
      zIndex:100
    },
    listItemsStyle: {
      display:'flex', 
      flexDirection:'column'
    },
    title: {
      color: '#000000', 
      fontSize: '1.5rem' 
    },
    pointStyle: {
      fontSize: '.9em',
      marginBottom: '1em'
    },
    titleContainer: {
      display: 'flex', 
      width: '100%',
      boxSizing: 'border-box',
      paddingX: '1em',
    },
    loadingContainer: {
      width: '100%', 
      height: '100%',
      justifyContent: 'center', 
      paddingY: '1em'
    }
  };

  return (
    <Stack sx={styles.container} spacing={3}>
      <Box sx={styles.headerContainer}>
        <BackButton returnCallback={goBack} />
      </Box>
      <Box sx={styles.titleContainer}>
        <Typography sx={styles.title} variant='h1' gutterBottom>
          {t('stepPath.page-label')}
        </Typography>
      </Box>
      {status === 'success' &&
      <Box display='flex' flexDirection='column'>
        <List
          sx={{
            width: '100%',
          }}
        >
          <ListItem>
            <PointWithLabelRoad 
              label={origin.label} 
              time={timeDepart}
              subtitle={features[0].properties.instruction}
            >
              <FontAwesomeIcon 
                id='origin-marker-icon' 
                icon={faMapMarkerAlt} 
                color='green' 
                style={{fontSize: '1.6em'}}
              />
            </PointWithLabelRoad>
          </ListItem>
          {features.map((feature, i) => (
            <ListItem key={i} sx={styles.listItemsStyle}>
              {renderItemList(i, feature, features[i+1])}
            </ListItem>
          ))}
          <ListItem>
            <PointWithLabelRoad label={dest.label} time={timeDest}>
              <FontAwesomeIcon 
                id='dest-marker-icon'
                icon={faMapMarkerAlt} 
                color='blue'
                style={{fontSize: '1.6em'}}
              />
            </PointWithLabelRoad>
          </ListItem>
        </List> 
      </Box>
      }
      {status === 'loading' && 
    (
      <Box display='flex' sx={styles.loadingContainer}>
        <CircularProgress  />
      </Box>
    )
      }
    </Stack>
  );
};

